import React, { useState, useEffect, useRef } from 'react';
import Video, { createLocalVideoTrack, createLocalTracks } from 'twilio-video';
import cx from 'classnames';
import { withStyles, fade } from '@material-ui/core/styles';
import RecordIcon from '@material-ui/icons/FiberManualRecord';
import Alert from '@material-ui/lab/Alert';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Fab from '@material-ui/core/Fab';
import CallEnd from '@material-ui/icons/CallEnd';
import ScreenShare from '@material-ui/icons/ScreenShare';
import MicIcon from '@material-ui/icons/Mic';
import MicOffIcon from '@material-ui/icons/MicOff';
import VideocamIcon from '@material-ui/icons/Videocam';
import VideocamOffIcon from '@material-ui/icons/VideocamOff';
import StopScreenShareIcon from '@material-ui/icons/StopScreenShare';
import padStart from 'lodash/padStart';
import useSnack from 'hooks/useSnack';
import Participant from './Participant';
import Chat from './Chat';
import {
  getTracks,
  detachParticipantTracks,
  detachTrack,
  calculateThumbnailSizeForTileView,
  getTileViewGridDimensions,
} from './utils';
import useTimers from './useTimers';
import styles from './styles';
import ChatIcon from '@material-ui/icons/Forum';
import Drawer from '@material-ui/core/Drawer';
import ScreenTrack from './ScreenTrack';

const getErrorMessage = ({ name, message, constraintName, constraint }) => {
  switch (name) {
    case 'NotFoundError':
    case 'DevicesNotFoundError': {
      return 'Audio/video device not found. Please make sure you have a microphone and webcam connected';
    }
    case 'NotReadableError':
    case 'TrackStartError': {
      return 'Unable to access the webcam or mic. Please make sure they are not in use (by Skype, etc.).';
    }
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return `Your device does not support required capability - ${constraint ||
        constraintName}. This may be due to outdated hardware.`;
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Please allow Webcam and Microphone access to continue.';
    default:
      return message;
  }
};

const secondsToPretty = (sec) => {
  if (sec < 60) return `00:${padStart(sec, 2, 0)}`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${padStart(m, 2, 0)}:${padStart(s, 2, 0)}`;
};

const Room = ({ roomName, token, handleLogout, classes, settings }) => {
  const [room, setRoom] = useState(null);
  const [permissionError, setPermissionError] = useState();
  const [sharingScreen, setSharingScreen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [screenTrack, setScreenTrack] = useState(null);
  const [isMute, setMute] = useState(false);
  const [isVideoOn, setVideoToggle] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [dominantSpeaker, setDominantSpeaker] = useState(null);
  const { duration } = useTimers(room, participants);
  const screenRef = useRef();
  const snack = useSnack();

  useEffect(() => {
    Video.connect(token, {
      name: roomName,
      audio: settings
        ? {
            deviceId: settings.audioinput,
          }
        : true,
      video: { height: 720, width: 1280, deviceId: settings?.videoinput },
      bandwidthProfile: {
        video: {
          mode: 'collaboration',
          trackSwitchOffMode: 'predicted',
          dominantSpeakerPriority: 'high',
          maxSubscriptionBitrate: 2400000,
          maxTracks: 10,
          renderDimensions: {
            high: { width: 1280, height: 720 },
            standard: { width: 640, height: 480 },
            low: { width: 320, height: 240 },
          },
        },
      },
      networkQuality: { local: 1, remote: 1 },
      maxAudioBitrate: 16000,
      preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
      dominantSpeaker: true,
    })
      .then((room) => {
        setRoom(room);
        room.participants.forEach(participantConnected);
        room.on('participantConnected', participantConnected);
        room.on('participantDisconnected', participantDisconnected);
        room.on('dominantSpeakerChanged', dominantSpeakerChanged);
        room.on('disconnected', (room) => {
          room.localParticipant.tracks.forEach((publication) => {
            publication.track.stop();
            publication.track.detach();
          });
          room.participants.forEach(detachParticipantTracks);
          handleLogout();
        });
      })
      .catch((e) => {
        setPermissionError(getErrorMessage(e));
      });

    return () => {
      setRoom((currentRoom) => {
        if (currentRoom && currentRoom.localParticipant.state === 'connected') {
          console.log('cleanup');
          currentRoom.disconnect();
          currentRoom.off('participantConnected', participantConnected);
          currentRoom.off('participantDisconnected', participantDisconnected);
          currentRoom.off('dominantSpeakerChanged', dominantSpeakerChanged);
          return null;
        } else {
          return currentRoom;
        }
      });
    };
  }, [roomName, token]);

  useEffect(() => {
    if (screenTrack) {
      screenTrack.publication.track.attach(screenRef.current);
    }
  }, [screenTrack]);

  const trackPublished = (publication, participant) => {
    console.log(
      `RemoteParticipant ${participant.identity} published a RemoteTrack: ${publication}`,
    );

    publication.on('subscribed', (track) => {
      console.log(`LocalParticipant subscribed to a RemoteTrack: ${track}`);
      if (track.kind === 'video' && publication.trackName === 'screen') {
        setScreenTrack({ publication, participant });
      }
    });

    publication.on('unsubscribed', (track) => {
      console.log(
        `LocalParticipant unsubscribed from a RemoteTrack: ${track.sid}`,
      );
      if (track.name === 'screen') {
        setScreenTrack();
        track.detach();
      }
    });
  };

  const dominantSpeakerChanged = (participant) => {
    if (participant) {
      console.log('The new dominant speaker in the Room is: ', participant.identity);
      setDominantSpeaker(participant);
    }
  };

  const participantConnected = (participant) => {
    setParticipants((prevParticipants) => [...prevParticipants, participant]);

    participant.tracks.forEach((publication) => {
      trackPublished(publication, participant);
    });

    participant.on('trackPublished', (publication) => {
      trackPublished(publication, participant);
    });

    participant.on('trackUnpublished', (publication) => {
      console.log(
        `RemoteParticipant ${participant.identity} unpublished a RemoteTrack: ${publication}`,
      );
      publication.track && publication.track.detach();
      if (publication.trackName === 'screen') {
        setScreenTrack();
      }
    });
  };

  const participantDisconnected = (participant) => {
    console.log(
      `RemoteParticipant ${participant.identity} disconnected: ${participant}`,
    );
    setParticipants((prevParticipants) =>
      prevParticipants.filter((p) => p !== participant),
    );
    setDominantSpeaker(prev => {
      console.log('remove dominant speaker ', participant.identity);
      return prev === participant ? null : prev
    });
  };

  const onShareScreen = async () => {
    if (!navigator.mediaDevices.getDisplayMedia) {
      return snack.error('Not supported by your browser.');
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });
      stream.oninactive = onScreenShareEnd;
      const tracks = stream.getVideoTracks();
      const newScreenTrack = tracks[0];
      room.localParticipant.publishTrack(newScreenTrack, {
        name: 'screen',
      });
      setSharingScreen(true);
    } catch (e) {
      const { message, name } = e;
      snack.error(
        name === 'NotAllowedError'
          ? 'You must allow to share screen.'
          : message,
      );
    }
  };

  const onScreenShareEnd = () => {
    getTracks(room.localParticipant).forEach((t) => {
      if (t.name === 'screen') {
        room.localParticipant.unpublishTrack(t);
        t.stop();
        detachTrack(t);
        setSharingScreen(false);
      }
    });
    setScreenTrack();
  };

  const toggleMute = () => {
    const muted = !isMute;
    room.localParticipant.audioTracks.forEach((t) => {
      muted ? t.track.disable() : t.track.enable();
    });
    setMute(muted);
  };

  const toggleVideo = async () => {
    const isOff = !isVideoOn;

    if (isOff) {
      const track = await createLocalVideoTrack({
        facingMode: 'user',
        height: 720,
        width: 1280,
        deviceId: settings?.videoinput,
      }).catch((e) => snack.error(e.message));
      room.localParticipant.publishTrack(track);
      setVideoToggle(isOff);
    } else {
      room.localParticipant.videoTracks.forEach((t) => {
        if(t.track.name !== 'screen') {
        t.track.stop();
        t.unpublish();
        }
      });
      setVideoToggle(isOff);
    }
  };

  const onDisconnect = () => {
    room.disconnect();
  };

  if (permissionError) {
    return (
      <Alert variant="standard" color="error">
        {permissionError}
      </Alert>
    );
  }

  if (!room) return <CircularProgress />;

  const totalParticipants = participants.length + 1;
  const { visibleRows, columns } = getTileViewGridDimensions(
    totalParticipants,
    3,
  );

  const { innerHeight = 0, innerWidth = 0 } = window;
  const layout = calculateThumbnailSizeForTileView({
    clientHeight: innerHeight - 20,
    clientWidth: innerWidth,
    visibleRows,
    columns,
  });

  return (
    <div className={classes.room}>
      <Box
        className={cx(classes.content, { [classes.contentShift]: showChat })}
      >
        <div className={classes.header}>
          <div className={classes.duration}>{secondsToPretty(duration)}</div>
          <div className={classes.recording}>
            <RecordIcon
              fontSize="small"
              color="secondary"
              className="blinking"
            />{' '}
            RECORDING
          </div>
        </div>
        <ScreenTrack
          isLocal={sharingScreen}
          screenTrack={screenTrack}
          screenRef={screenRef}
          onScreenShareEnd={onScreenShareEnd}
        />
        <div className={cx('participants', { small: !!screenTrack || sharingScreen })}>
          <Participant
            isLocal
            participant={room.localParticipant}
            room={room}
            layout={layout}
            dominantSpeaker={dominantSpeaker}
            setDominantSpeaker={setDominantSpeaker}
          />
          {participants.map((p) => (
            <Participant
              participant={p}
              key={p.identity}
              layout={layout}
              dominantSpeaker={dominantSpeaker}
              setDominantSpeaker={setDominantSpeaker}
            />
          ))}
        </div>
        <Paper className={classes.footer}>
          <Box>
            <Typography variant="body2" color="textSecondary"></Typography>
            <Typography variant="caption"></Typography>
          </Box>
          <Box>
            <Fab
              
              classes={{
                root: classes.fab,
              }}
              onClick={toggleVideo}
            >
              {isVideoOn ? <VideocamIcon fontSize='small' /> : <VideocamOffIcon fontSize="small" />}
            </Fab>
            <Fab
              
              classes={{
                root: classes.fab,
              }}
              onClick={onDisconnect}
            >
              <CallEnd color="secondary" fontSize="small" />
            </Fab>
            <Fab
              
              classes={{
                root: classes.fab,
              }}
              onClick={toggleMute}
            >
              {isMute ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </Fab>
          </Box>
          <Box textAlign="right">
            {!screenTrack && !sharingScreen && (
              <Fab
                
                classes={{
                  root: classes.fab,
                }}
                onClick={onShareScreen}
              >
                <ScreenShare fontSize="small" />
              </Fab>
            )}
            {sharingScreen && (
              <Fab
                
                classes={{
                  root: classes.fab,
                }}
                onClick={onScreenShareEnd}
              >
                <StopScreenShareIcon fontSize="small" />
              </Fab>
            )}
            {room && (
              <Fab
                
                onClick={() => setShowChat(true)}
                classes={{
                  root: classes.fab,
                }}
              >
                <ChatIcon fontSize="small" />
              </Fab>
            )}
          </Box>
        </Paper>
      </Box>
      {room && (
        <Drawer
          anchor="right"
          classes={{
            paper: classes.drawerPaper,
          }}
          variant="persistent"
          open={showChat}
          className={classes.drawer}
        >
          <Chat
            show={showChat}
            roomId={room.sid}
            onClose={() => setShowChat(false)}
          />
        </Drawer>
      )}
    </div>
  );
};

export default withStyles(styles)(Room);
